<template>
  <div style="background: #f2f2f2">
    <div
      class="relative w-full flex flex-col justify-start"
      v-if="!showOnlyGlobal"
    >
      <div class="flex bg-white border-grey-200">
        <gd-tabs
          class="mx-4"
          :tabs="tabs"
          :currentTab="currentTab"
          :wrapper-class="'gs-default-tabs'"
          :tab-class="mainTabClass"
          :tab-active-class="'border-primary border-b-2 text-primary gs-default-tabs-item-active'"
          :line-class="'gs-default-tabs-active-line'"
          @onClick="handleTabChange"
          :key="$t('frontend_groovemail_campaign_tabs_account')"
        />
      </div>
    </div>
    <div>
      <div class="rounded w-full relative">
        <!-- Loader -->
        <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
          <gd-loader :label="$t('frontend_loading')"/>
        </div>

        <div class="w-full relative" v-if="showInternalBanner">
          <gd-banner
            class="fixed"
            v-if="!canUsePremiumBuilder && showLimitBanner"
            :bannerMsg="bannerConfig.message"
            :bannerTextColor="bannerConfig.textColor"
            :bannerSize="bannerConfig.size"
            :bannerColor="bannerConfig.color"
            :bannerActions="bannerConfig.bannerActions"
          >
            <template slot="action-butons">
              <div class="mr-4">
                <gd-button
                  :variant="bannerConfig.actionItems.actionBtnType === 'primary' ? 'primary' : 'dark'"
                  size="auto"
                  @click="goToUpgradePage"
                  class="ml-2"
                > {{ $t(bannerConfig.actionItems.actionBtnText) }}
                </gd-button>
              </div>
              <div class="cursor-pointer" @click="updateLimitBanner(false)">
                <span
                  class="fas fa-times text-lg"
                  :class="[bannerConfig.textColor]"
                ></span>
              </div>
            </template>
          </gd-banner>
        </div>
        <div v-if="currentTab === 'global'">
          <template-selector
            :key="currentTab"
            :tabType="currentTab"
            :showOnlyGlobal="showOnlyGlobal"
            :url="templatesUrl(this.currentTab)"
            :filtersHeight="filtersHeight"
            :templatesHeight="templatesHeight"
            :gridView="gridView"
            @showLoader="showLoader"
            @hideLoader="hideLoader"
            @selectedTemplate="selectedTemplate"
          ></template-selector>
        </div>
        <div v-if="currentTab === 'my-templates'">
          <template-selector
            :key="currentTab"
            :tabType="currentTab"
            :showOnlyGlobal="showOnlyGlobal"
            :url="templatesUrl(this.currentTab)"
            :filtersHeight="filtersHeight"
            :templatesHeight="templatesHeight"
            :gridView="gridView"
            @showLoader="showLoader"
            @hideLoader="hideLoader"
            @selectedTemplate="selectedTemplate"
            @editTemplate="editTemplate"
            @renameTemplate="renameTemplate"
            @cloneTemplate="cloneTemplate"
            @deleteTemplate="deleteTemplate"
          ></template-selector>
        </div>
        <div v-if="currentTab === 'saved-emails'">
          <template-selector
            :key="currentTab"
            :tabType="currentTab"
            :showOnlyGlobal="showOnlyGlobal"
            :url="templatesUrl(this.currentTab)"
            :filtersHeight="filtersHeight"
            :templatesHeight="templatesHeight"
            :gridView="gridView"
            @showLoader="showLoader"
            @hideLoader="hideLoader"
            @selectedTemplate="selectedTemplate"
          ></template-selector>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import auth from '@groovepages/gd-lib/auth';
import GDBanner from '@groovepages/gd-ui-kit/components/GDBanner';
import TemplateSelector from './template-selector.vue';

export default {
  middleware: 'auth',
  name: 'template-container',
  props: {
    showOnlyGlobal: {
      required: false,
    },
    filtersHeight: {
      required: false,
      default: 'calc(100vh - 260px)',
    },
    templatesHeight: {
      required: false,
      default: 'calc(100vh - 120px)',
    },
    showInternalBanner: {
      required: false,
      default: true,
    },
    gridView: {
      required: false,
      default: true,
    },
  },

  data() {
    return {
      currentTab: 'global',
      showLimitBanner: true,
      bannerConfig: {
        message: this.$t('frontend_groovemail_premium_builder_access_message'),
        size: 'full',
        color: 'bg-primary-dark',
        textColor: 'text-primary-invert',
        bannerActions: true,
        actionItems: {
          actionBtnType: 'secondary',
          actionBtnText: 'frontend_groovemail_upgrade_btn',
        },
      },
      isLoading: false,
    };
  },

  components: {
    TemplateSelector,
    'gd-banner': GDBanner,
  },

  computed: {
    tabs() {
      const builderTabs = [];
      builderTabs.push({
        title: `<span class="text-sm font-semibold mr-2">&nbsp;
             ${this.$t(
               'frontend_groovemail_admin_global_templates_page_header'
             )} <span>`,
        value: 'global',
      });
      if (this.canUsePremiumBuilder) {
        builderTabs.push({
          title: `<span class="text-sm font-semibold mr-2">&nbsp;
            ${this.$t('frontend_groovemail_my_templates_page_header')} <span>`,
          value: 'my-templates',
        });
        builderTabs.push({
          title: `<span class="text-sm font-semibold mr-2">&nbsp;
            ${this.$t('frontend_groovemail_saved_past_emails_page_header')} <span>`,
          value: 'saved-emails',
        });
      }
      return builderTabs;
    },

    mainTabClass() {
      return 'pb-4 gs-default-tabs-item';
    },

    // new plans based limit check
    canUsePremiumBuilder() {
      return auth.fetchFeatureValue('groovemail_can_use_email_builder') === 1;
    },

  },

  mounted() {
    this.updateLimitBanner(true);
  },

  methods: {
    // show loader
    showLoader() {
      if(!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader() {
      if(this.isLoading) {
        this.isLoading = false;
      }
    },

    handleTabChange(newTab) {
      this.currentTab = newTab;
    },

    selectedTemplate(templateId) {
      let templateType = '';
      if (this.currentTab === 'global') {
        templateType = 'template';
      } else if (this.currentTab === 'my-templates') {
        templateType = 'user-template';
      } else if (this.currentTab === 'saved-emails') {
        templateType = 'user-email';
      }
      this.$emit('selectedTemplate', templateId, templateType);
    },

    templatesUrl(type) {
      const gdMailAPIsUrl = window.config.apiUrl;

      if (type === 'global') {
        return `${gdMailAPIsUrl}/email-templates`;
      }
      if (type === 'my-templates') {
        return `${gdMailAPIsUrl}/user/template/list`;
      }
      if (type === 'saved-emails') {
        return `${gdMailAPIsUrl}/email/template/list`;
      }
      return '';
    },

    updateLimitBanner(show) {
      this.showLimitBanner = show;
    },

    goToUpgradePage() {
      const user = auth.authenticatedUser();
      const url = window.gdEnv[`UPGRADE_URL_${user?.metadata?.region?.toUpperCase()}`] || window.gdEnv[`UPGRADE_URL`];

      window.open(url, '_blank');
    },

    editTemplate(templateId) {
      this.$emit('editTemplate', templateId);
    },

    renameTemplate(templateId){
      this.$emit('renameTemplate', templateId);
    },

    cloneTemplate(template) {
      this.$emit('cloneTemplate', template);
    },

    deleteTemplate(templateId) {
      this.$emit('deleteTemplate', templateId);
    },
  },
};
</script>

<style lang="scss">
.template-folder-icon {
  font-size: 64px !important;
}
</style>
